import {
  AppBridge,
  PostMessageTransport,
} from "@modelcontextprotocol/ext-apps/app-bridge";
import { EmptyResultSchema } from "@modelcontextprotocol/core";
import type { CallToolResult } from "@modelcontextprotocol/client";

export type WidgetPackage = { version: 1; bundle: string; digest: string };
export type WidgetHost = {
  refresh(): Promise<void>;
  theme(dark: boolean): void;
  close(): void;
};
export function connectWidget(
  frame: HTMLIFrameElement,
  options: {
    bootstrap: string;
    package: WidgetPackage;
    proxyUrl: string;
    dark: boolean;
    call(
      name: string,
      args: Record<string, unknown>,
      signal: AbortSignal,
    ): Promise<CallToolResult>;
    onSize(height: number): void;
    onError(message: string): void;
  },
): WidgetHost {
  const abort = new AbortController();
  let closed = false,
    ready = false,
    inflight = 0;
  const bridge = new AppBridge(
    null,
    { name: "LawWorks", version: "1.0.0" },
    { serverTools: {}, logging: {} },
    {
      hostContext: {
        theme: options.dark ? "dark" : "light",
        displayMode: "inline",
      },
    },
  );
  const timeout = window.setTimeout(
    () => fail("This widget could not start. Reload it to try again."),
    12000,
  );
  function fail(message: string) {
    if (closed) return;
    options.onError(message);
    close();
  }
  bridge.oncalltool = async ({ name, arguments: args }) => {
    if (closed || abort.signal.aborted)
      throw new Error("This widget has closed.");
    if (inflight >= 4 || JSON.stringify(args ?? {}).length > 64000)
      return {
        isError: true,
        content: [
          {
            type: "text",
            text: "Too many reports or filters. Wait for the current reads.",
          },
        ],
      };
    inflight++;
    try {
      const result = await options.call(name, args ?? {}, abort.signal);
      if (closed) throw new Error("This widget has closed.");
      if (JSON.stringify(result).length > 1_100_000)
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: "This report is too large. Request fewer rows.",
            },
          ],
        };
      if (result._meta?.code === "access_changed")
        fail("Your access changed. Reload the page to continue.");
      return result;
    } catch (error) {
      if (!closed && !abort.signal.aborted)
        return {
          isError: true,
          content: [
            {
              type: "text",
              text:
                error instanceof Error
                  ? error.message
                  : "The report is unavailable. Try Refresh.",
            },
          ],
        };
      throw error;
    } finally {
      inflight--;
    }
  };
  bridge.onsandboxready = () => {
    if (!closed)
      void bridge
        .sendSandboxResourceReady({
          html: options.bootstrap.replace(
            "__WIDGET_BUNDLE_HASH__",
            btoa(
              String.fromCharCode(
                ...options.package.digest
                  .match(/../g)!
                  .map((value) => parseInt(value, 16)),
              ),
            ),
          ),
        })
        .catch(() => fail("The widget requires a page reload."));
  };
  bridge.oninitialized = async () => {
    if (closed) return;
    window.clearTimeout(timeout);
    ready = true;
    try {
      await bridge.sendToolInput({ arguments: {} });
      if (!closed)
        await bridge.sendToolResult({
          content: [],
          _meta: { widget: options.package },
        });
    } catch {
      if (!closed) fail("This widget could not start. Reload it to try again.");
    }
  };
  bridge.onsizechange = ({ height }) => {
    if (!closed && height && Number.isFinite(height))
      options.onSize(Math.min(2000, Math.max(160, height)));
  };
  bridge.onloggingmessage = ({ level }) => {
    if (level === "error" || level === "critical" || level === "emergency")
      fail("The widget stopped. Reload it to try again.");
  };
  // No message, navigation, sampling, context-update, or product-write handlers.
  // With no MCP client attached, upstream AppBridge cannot forward other methods.
  void bridge
    .connect(
      new PostMessageTransport(frame.contentWindow!, frame.contentWindow!),
    )
    .then(() => {
      if (!closed) frame.src = options.proxyUrl;
    })
    .catch(() => fail("The widget could not connect. Reload it to try again."));
  const monitor = window.setInterval(() => {
    if (closed || !ready || document.visibilityState !== "visible") return;
    void bridge
      .request({ method: "ping" }, EmptyResultSchema, { timeout: 2000 })
      .catch(() =>
        fail("The widget stopped responding. Reload it to try again."),
      );
  }, 5000);
  function close() {
    if (closed) return;
    closed = true;
    ready = false;
    window.clearTimeout(timeout);
    window.clearInterval(monitor);
    abort.abort();
    // Disconnect synchronously before this WindowProxy can belong to another
    // mount. A delayed teardown message must never reach the next instance.
    void bridge.close();
    frame.src = "about:blank";
  }
  return {
    refresh: async () => {
      if (!closed && ready)
        await bridge.sendToolResult({ content: [], _meta: { refresh: true } });
    },
    theme: (dark) =>
      bridge.setHostContext({
        theme: dark ? "dark" : "light",
        displayMode: "inline",
      }),
    close,
  };
}
